import { AnimatePresence, motion } from 'framer-motion'
import mammoth from 'mammoth'
import { ReactNode, useEffect, useCallback, useState } from 'react'
import {
  Button,
  CardBody,
  CardTitle,
  Col,
  Container,
  ListGroup,
  Modal,
  Row,
  Spinner,
  Alert,
} from 'react-bootstrap'
import { ChevronDown, ChevronRight } from 'react-bootstrap-icons'
import AmlOrderModal from '../components/AmlOrderModal'
import TransparenzregisterModal from '../components/TransparenzregisterModal'
import KAccess from '../components/KAccess'
import KButton from '../components/KButton'
import ServiceCostSubtitle from '../components/ServiceCostSubtitle'
import KLayerNavigation from '../components/KLayerNavigation'
import IdentityModal from '../components/KModal'
import KSignModal from '../components/KSign'
import FileUploader from '../components/KUploadFile'
import {
  apiRequest,
  formatDate,
  getUserAccess,
  isPiiEncryptionEnabled,
  isUserLoggedIn,
  setAccessToken,
  setUserAccess,
  showKAlert,
} from '../helpers'
import { t } from '../locale'
import { Client, DSSignRequest, UserAccess } from '../react-app-env'
import {
  createIdentityRequestService,
  deleteIdentityRequestService,
  fetchAccess,
  fetchIdentityParams,
  validatePinCode,
} from '../service/client'
import { isDsSignAvailable } from '../service/user'
import { fetchServiceCosts, ServiceCosts } from '../service/subscription'
import { ensureEnoughCredit } from '../helpers/credit.helper'
import ZefixResultModal from '../components/zefix/ZefixResultModal'
import ZefixAuditLogModal from '../components/zefix/ZefixAuditLogModal'
import { zefixCompanyExtractByClientId, zefixCompanySearch } from '../service/zefix'
import UidCheckModal from '../components/uid/UidCheckModal'
import UidAuditLogModal from '../components/uid/UidAuditLogModal'
import { uidGetByClientId } from '../service/uid'
import { computeUidCheckVariant } from '../helpers/uidCheckVariant'
import { downloadKycFile } from '../helpers/downloadKycFile'
import TransparenzChModal from '../components/transparenz-ch/TransparenzChModal'
import TopographModal from '../components/topograph/TopographModal'
import { XlsxViewerPreview } from '../components/extend/xlsx-viewer'
import React from 'react'
import SharePointFilePickerModal from '../components/SharePointFilePickerModal'
import { isMicrosoftEnabled } from '../service/microsoft'
import {
  copySharePointFile,
  deleteSharePointFile,
  getSharePointFiles,
  hashSharePointFile,
  linkSharePointFile,
  LinkedSharePointFile,
  openSharePointFile,
  SharePointDriveItem,
  uploadDocumentToSharePoint,
} from '../service/sharepoint'

type DocumentFile = {
  name: string
  url: string
  type:
    | 'ds-sign'
    | 'ds-identity'
    | 'transparenzregister'
    | 'transparenz-ch'
    | 'topograph'
    | 'Handelsregister'
    | 'zefix'
    | 'stammdaten_additionalFormFiles'
    | 'stammdaten_files'
  uploadedAt?: string
  uploadedBy?: string
}

type CountryGroup = 'DE' | 'CH' | 'OTHER'

type ActionButtonConfig = {
  id: string
  group: CountryGroup
  show: boolean
  size?: 'sm' | 'lg'
  icon?: string
  label?: string
  variant?:
    | 'secondary'
    | 'success'
    | 'warning'
    | 'danger'
    | 'info'
    | 'white'
    | 'outline-success'
    | 'outline-danger'
    | 'outline-warning'
  className?: string
  title: string
  onlyIcon?: boolean
  onClick: () => void
}

type VariantMap = Record<string, 'white' | 'success' | 'warning' | 'danger'>

// A register document (Transparenzregister/Handelsregister, DE or CH) keeps its button
// green for one year from the day the document was received; after that the button falls
// back to its neutral state so a fresh extract can be ordered.
const REGISTER_DOCUMENT_VALIDITY_MS = 365 * 24 * 60 * 60 * 1000

const SECTIONS: DocumentFile['type'][] = [
  'ds-sign',
  'ds-identity',
  'transparenzregister',
  'transparenz-ch',
  'Handelsregister',
  'zefix',
  'topograph',
  'stammdaten_additionalFormFiles',
  'stammdaten_files',
]

export default function ImmobilienTable() {
  const [docsBySection, setDocsBySection] = useState<Record<string, DocumentFile[]>>({
    'ds-sign': [],
    'ds-identity': [],
    transparenzregister: [],
    'transparenz-ch': [],
    Handelsregister: [],
    zefix: [],
    topograph: [],
    stammdaten_additionalFormFiles: [],
    stammdaten_files: [],
  })

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'ds-sign': true,
    'ds-identity': true,
    transparenzregister: true,
    'transparenz-ch': true,
    Handelsregister: true,
    zefix: true,
    topograph: true,
    stammdaten_additionalFormFiles: true,
    stammdaten_files: true,
  })

  const userAccess: UserAccess = getUserAccess()
  const [variantIdentity, setVariantIdentity] = useState<VariantMap>({})
  const [identityRequests, setIdentityRequests] = useState<Record<string, any>>({})
  const [variantTransparenz, setVariantTransparenz] = useState<VariantMap>({})
  const [showModal, setShowModal] = useState(false)

  const [client, setClient] = useState<Client>()
  const [signModal, setSignModal] = useState(false)
  const [previewType, setPreviewType] = useState<'txt' | 'excel' | 'word' | null>(null)
  const [previewText, setPreviewText] = useState<string | null>(null)
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)
  const [previewFile, setPreviewFile] = useState<ArrayBuffer | null>(null)
  const [previewFilename, setPreviewFilename] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [currentDocument, setCurrentDocument] = useState('')
  const [signRequests, setSignRequests] = useState<DSSignRequest[]>([])
  const [userNames, setUserNames] = useState<Record<string, string>>({})

  const [pin, setPin] = useState('')
  const [access, setAccess] = useState(false)
  const [pinRequired, setPinRequired] = useState(true)
  const [accessTitle, setAccessTitle] = useState<string | undefined>(undefined)
  const [accessText, setAccessText] = useState<string | undefined>(undefined)
  const [accessWarningText, setAccessWarningText] = useState<string | undefined>(undefined)
  const [accessSubtitle, setAccessSubtitle] = useState<ReactNode>(undefined)

  const [pendingAction, setPendingAction] = useState<(() => Promise<void>) | null>(null)
  const [transparenzModal, setTransparenzModal] = useState(false)

  const pathSegments = window.location.pathname.split('/')
  const clientNumber = pathSegments[3]
  const [dsSignFilter, setDsSignFilter] = useState<'all' | 'signed' | 'unsigned'>('all')
  const [protocolFiles, setProtocolFiles] = useState<Record<string, string>>({})
  // Maps a topograph diagram PDF name -> its "graph-*.txt" raw-data file name.
  const [graphDataFiles, setGraphDataFiles] = useState<Record<string, string>>({})

  const [amlModal, setAmlModal] = useState(false)

  const [dsSignAvailable, setDsSignAvailable] = useState(false)

  const [showAuditLogModal, setShowAuditLogModal] = useState(false)
  const [zefixResults, setZefixResults] = useState<unknown[]>([])
  const [zefixInitialDetails, setZefixInitialDetails] = useState<Record<string, unknown> | null>(
    null
  )
  const [showZefixModal, setShowZefixModal] = useState(false)

  const [showUidModal, setShowUidModal] = useState(false)
  const [showUidAuditLogModal, setShowUidAuditLogModal] = useState(false)
  const [uidCheckVariant, setUidCheckVariant] = useState<
    'white' | 'warning' | 'success' | 'danger'
  >('white')
  const [showTransparenzChModal, setShowTransparenzChModal] = useState(false)
  const [showTopographModal, setShowTopographModal] = useState(false)
  const [serviceCosts, setServiceCosts] = useState<ServiceCosts | null>(null)

  const [isOpenExcelPreview, setIsOpenExcelPreview] = React.useState(false)

  const [showSharePointModal, setShowSharePointModal] = useState(false)
  const [sharePointFiles, setSharePointFiles] = useState<LinkedSharePointFile[]>([])
  const [microsoftEnabled, setMicrosoftEnabled] = useState(true)

  useEffect(() => {
    isMicrosoftEnabled().then(setMicrosoftEnabled)
  }, [])

  useEffect(() => {
    if (!microsoftEnabled || !client?._id) return
    void loadSharePointFiles(String(client._id))
  }, [client?._id, microsoftEnabled])

  const [globalSettings, setGlobalSettings] = useState({
    transparenzregisterChEnabled: true,
  })

  const fetchTransparenzStatusForClient = useCallback(async (clientId: string) => {
    try {
      const res = await apiRequest(
        `${process.env.REACT_APP_API_URL}/transparenzregister/requests?clientId=${clientId}`,
        'GET'
      )

      let status = res?.status

      if (Array.isArray(res) && res.length > 0) {
        const last = res[0]
        status = last?.status
      }

      const variant = status === 'finished' ? 'success' : status === 'pending' ? 'warning' : 'white'

      setVariantTransparenz(prev => ({
        ...prev,
        [clientId]: variant,
      }))
    } catch (e) {
      setVariantTransparenz(prev => ({
        ...prev,
        [clientId]: 'white',
      }))
    }
  }, [])

  const fetchIdentityStatusForClient = useCallback(async (clientId: string) => {
    try {
      const res = await fetchIdentityParams(clientId)

      setIdentityRequests(prev => {
        if (!res?.identity) {
          const { [clientId]: _, ...rest } = prev
          return rest
        }

        return {
          ...prev,
          [clientId]: res.identity,
        }
      })

      setVariantIdentity(prev => ({
        ...prev,
        [clientId]: res?.ok && res?.identity ? 'success' : res?.identity ? 'warning' : 'white',
      }))
    } catch (e) {
      setVariantIdentity(prev => ({
        ...prev,
        [clientId]: 'white',
      }))

      setIdentityRequests(prev => {
        const { [clientId]: _, ...rest } = prev
        return rest
      })
    }
  }, [])

  // Refreshes the badges/state derived from a freshly loaded client (Transparenzregister, DS|Identity, UID checks).
  const syncClientStatus = useCallback(
    async (statusClient: Client) => {
      if (!statusClient?._id) return

      localStorage.setItem('clientId', statusClient._id)
      localStorage.setItem('name', statusClient.name || '')
      localStorage.setItem('registernummer', statusClient.registernummer || '')

      fetchTransparenzStatusForClient(statusClient._id)
      fetchIdentityStatusForClient(statusClient._id)

      try {
        const uidResults = await uidGetByClientId(statusClient._id)
        if (Array.isArray(uidResults) && uidResults.length > 0) {
          setUidCheckVariant(
            computeUidCheckVariant(statusClient, uidResults[uidResults.length - 1])
          )
        }
      } catch (e) {
        // keep the current uid check variant on failure
      }
    },
    [fetchTransparenzStatusForClient, fetchIdentityStatusForClient]
  )

  // Resolves display names for any DS|Sign requesters not already in state.
  const fetchUserNames = useCallback(async (requests: DSSignRequest[]) => {
    const userIds = [...new Set(requests.map(r => r.user_id).filter(Boolean))] as string[]

    if (userIds.length === 0) return

    const fetched = await Promise.all(
      userIds.map(async userId => {
        const user = await apiRequest(
          `${process.env.REACT_APP_API_URL}/user-admin/user/${userId}`,
          'GET'
        )
        return user?.user
          ? ([userId, `${user.user.firstName} ${user.user.lastName}`] as const)
          : null
      })
    )

    const names = Object.fromEntries(fetched.filter(Boolean) as [string, string][])

    if (Object.keys(names).length > 0) {
      setUserNames(prev => ({ ...prev, ...names }))
    }
  }, [])

  const loadClient = useCallback(async () => {
    const result = await apiRequest(
      `${process.env.REACT_APP_API_URL}/client/number?clientNumber=${clientNumber}`,
      'GET'
    )

    if (result) {
      setClient(result)

      apiRequest(`${process.env.REACT_APP_API_URL}/files/kyc/documents`, 'POST', {
        firmename: result.name,
        clientId: localStorage.getItem('clientId'),
      })
        .then((res: { documents: DocumentFile[] }) => {
          const grouped: Record<string, DocumentFile[]> = {
            'ds-sign': [],
            'ds-identity': [],
            transparenzregister: [],
            'transparenz-ch': [],
            Handelsregister: [],
            zefix: [],
            topograph: [],
            stammdaten_additionalFormFiles: [],
            stammdaten_files: [],
          }

          const protocolMap: Record<string, string> = {}
          const protocolPrefix = 'Signature protocol - '

          res.documents.forEach(doc => {
            if (doc.name.startsWith(protocolPrefix)) {
              const originalName = doc.name.slice(protocolPrefix.length)
              protocolMap[originalName] = doc.name
            } else if (grouped[doc.type]) {
              grouped[doc.type].push(doc)
            } else {
              console.log('types: ' + doc.type)
              grouped['ds-sign'].push(doc)
            }
          })

          // Topograph delivers the ownership diagram as a rendered PDF plus a
          // "graph-*.txt" raw-data dump. Fold the txt into a download button on the
          // matching PDF row instead of listing it as its own document.
          const graphPdfPrefix = 'Eigentumerstrukturdiagramm-'
          const graphTxtPrefix = 'graph-'
          const graphDataMap: Record<string, string> = {}

          grouped.topograph.forEach(txt => {
            if (!txt.name.startsWith(graphTxtPrefix) || !txt.name.endsWith('.txt')) return
            const suffix = txt.name.slice(graphTxtPrefix.length).replace(/\.txt$/, '')
            const pdf = grouped.topograph.find(d => d.name === `${graphPdfPrefix}${suffix}.pdf`)
            if (pdf) graphDataMap[pdf.name] = txt.name
          })

          const foldedGraphTxts = Object.values(graphDataMap)
          grouped.topograph = grouped.topograph.filter(d => !foldedGraphTxts.includes(d.name))

          setProtocolFiles(protocolMap)
          setGraphDataFiles(graphDataMap)
          setDocsBySection(grouped)
        })
        .catch()
    }

    if (!result?._id) return

    const requests: DSSignRequest[] = await apiRequest(
      `${process.env.REACT_APP_API_URL}/sign/get/all`,
      'POST',
      {
        client_id: result._id,
      }
    )

    setSignRequests(requests)

    syncClientStatus(result)
    fetchUserNames(requests)
  }, [clientNumber, syncClientStatus, fetchUserNames])

  useEffect(() => {
    loadClient()
    fetchServiceCosts().then(costs => {
      if (costs) setServiceCosts(costs)
    })
  }, [clientNumber, loadClient])

  const cancel = async (document_id: string) => {
    try {
      await apiRequest(`${process.env.REACT_APP_API_URL}/sign/cancel`, 'POST', {
        document_id,
      })
    } catch (error) {}
  }

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/global-settings`)
      .then(response => (response.ok ? response.json() : null))
      .then(data => {
        if (data) {
          setGlobalSettings({
            transparenzregisterChEnabled: data.transparenzregisterChEnabled !== false,
          })
        }
      })
      .catch(() => {})
  }, [])

  const handleSignClose = () => {
    setSignModal(false)
    loadClient()
  }

  const handleIdentityClose = () => {
    setShowModal(false)
  }

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const findSignRequest = (doc: DocumentFile) => {
    const uploaded = doc.uploadedAt ? Date.parse(doc.uploadedAt) : NaN

    return signRequests
      .filter(r => r.document_name === doc.name)
      .filter(r => {
        const created = r.created_at ? Date.parse(r.created_at) : NaN
        return Number.isNaN(uploaded) || Number.isNaN(created) || created >= uploaded
      })
      .sort((a, b) => Date.parse(b.created_at ?? '') - Date.parse(a.created_at ?? ''))[0]
  }

  const getStatusClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'badge bg-primary'
      case 'canceled':
        return 'badge bg-danger'
      case 'finished':
        return 'badge bg-success'
      default:
        return 'badge bg-secondary'
    }
  }

  const handleLabel = (label: string) => {
    switch (label) {
      case 'ds-sign':
        return 'DS|Sign'
      case 'ds-identity':
        return 'DS|Identity'
      case 'transparenzregister':
        return 'Transparenzregister'
      case 'transparenz-ch':
        return 'Transparenzregister CH'
      case 'Handelsregister':
        return 'Handelsregister'
      case 'zefix':
        return 'Handelsregister Schweiz'
      case 'topograph':
        return 'KYB - ' + t('Unternehmensdaten')
      case 'stammdaten_additionalFormFiles':
        return 'Stammdaten - zusätzliche Formulare'
      case 'stammdaten_files':
        return 'Stammdaten - weitere Dokumente'
    }
  }

  const handleDelete = async (doc: DocumentFile, section: string) => {
    const confirmed = window.confirm(`Are you sure you want to delete the document "${doc.name}"?`)
    if (!confirmed) return

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/files/kyc/document`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: localStorage.getItem('clientId'),
          firmename: client?.name,
          type: section,
          filename: doc.name,
        }),
      })

      if (!res.ok) {
        throw new Error('Brisanje nije uspelo.')
      }

      setDocsBySection(prev => {
        const updated = { ...prev }
        updated[section] = updated[section].filter(d => d.name !== doc.name)
        return updated
      })
    } catch (err) {}
  }

  const createIdentityRequest = async () => {
    const userAccess = sessionStorage.getItem('userAccess') ?? ''
    const userAccessParsed = JSON.parse(userAccess)

    try {
      const result = await createIdentityRequestService({
        service: 'identity',
        identityData: {
          gender: localStorage.getItem('gender'),
          firstName: localStorage.getItem('firstName'),
          name: localStorage.getItem('name'),
          dateOfBirth: localStorage.getItem('dateOfBirth'),
          email: localStorage.getItem('email'),
          createdAt: new Date(),
          status: 'assigned',
          clientId: localStorage.getItem('clientId'),
          companyId: sessionStorage.getItem('companyInfo'),
          requestEmail: userAccessParsed['email'],
        },
      })

      if (result?.error === 'no_credit') {
        showKAlert('danger', 'notEnoughCredit')
        setShowModal(false)
        return
      }

      if (result?.success) {
        setVariantIdentity(prev => ({
          ...prev,
          [localStorage.getItem('clientId') as string]: 'warning',
        }))

        if (client?._id) {
          await fetchIdentityStatusForClient(client._id)
        }
      }

      setShowModal(false)
    } catch (error) {
      showKAlert('danger', 'An unexpected error occurred. Please try again.')
    }
  }

  const removeIdentityRequest = async () => {
    const clientId = client?._id || ''
    const identity = identityRequests[clientId]

    if (!identity) {
      return
    }

    const confirmed = window.confirm('Are you sure you want to remove the DS|Identity request?')
    if (!confirmed) return

    try {
      const result = await deleteIdentityRequestService({
        identityCompanyId: identity.identityCompanyId,
        identityRequestId: identity.identityRequestId,
        clientId: identity.clientId,
        subjectId: identity.subjectId,
      })

      if (result?.success) {
        setVariantIdentity(prev => ({
          ...prev,
          [clientId]: 'white',
        }))

        setIdentityRequests(prev => {
          const { [clientId]: _, ...rest } = prev
          return rest
        })

        showKAlert('success', 'DS|Identity request removed.')
        setShowModal(false)
      } else {
        showKAlert('danger', 'Failed to remove DS|Identity request.')
      }
    } catch (error) {
      showKAlert('danger', 'Failed to remove DS|Identity request.')
    }
  }

  const getAccess = async (service?: string) => {
    // Block before the PIN modal when the tenant can't afford the service.
    if (service && !(await ensureEnoughCredit(service))) return

    const result = await fetchAccess(localStorage.getItem('email'))

    if (!!result.accessToken) {
      setAccessToken(result.accessToken)
      setPin(result.pinCode)
      setPinRequired(result.pinRequired !== false)
      setAccess(true)
    }
  }

  const checkPin = async (pinCode: number) => {
    try {
      const res = await validatePinCode(pinCode)

      if (res?.userAccess) {
        setUserAccess(res.userAccess)
        setAccess(false)
        await pendingAction?.()
        setPendingAction(null)
      }
    } catch (err) {}
  }

  const handleAccessClose = () => {
    setAccess(false)
    setPendingAction(null)
    setAccessTitle(undefined)
    setAccessText(undefined)
    setAccessSubtitle(undefined)
    setAccessWarningText(undefined)
  }

  const handleDownload = async (filename: string, type: string) => {
    await downloadKycFile(
      localStorage.getItem('clientId') || '',
      client?.name || '',
      type,
      filename
    )
  }

  const handlePreview = async (doc: DocumentFile, section: string) => {
    try {
      const params = new URLSearchParams({
        clientId: localStorage.getItem('clientId') || '',
        firmename: client?.name || '',
        type: section,
        filename: doc.name,
      })
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/files/kyc/view?${params.toString()}`,
        { method: 'GET', credentials: 'include' }
      )
      if (!response.ok) return

      const contentType = response.headers.get('content-type') ?? 'application/octet-stream'
      const blob = await response.blob()
      const extension = doc.name.split('.').pop()?.toLowerCase() ?? ''

      if (extension === 'pdf') {
        const url = window.URL.createObjectURL(
          new Blob([blob], { type: contentType || 'application/pdf' })
        )
        window.open(url, '_blank')
        setTimeout(() => window.URL.revokeObjectURL(url), 10000)
        return
      }

      setPreviewFilename(doc.name)

      if (extension === 'txt') {
        setPreviewText(await blob.text())
        setPreviewType('txt')
        return
      }

      if (extension === 'xlsx' || extension === 'xls') {
        setPreviewFile(await blob.arrayBuffer())
        setPreviewType('excel')
        return
      }

      if (extension === 'docx') {
        setPreviewType('word')
        setPreviewLoading(true)
        const buffer = await blob.arrayBuffer()
        const result = await mammoth.convertToHtml({ arrayBuffer: buffer })
        setPreviewHtml(result.value)
        setPreviewLoading(false)
        return
      }

      // No in-app renderer for this type (images, old .doc, etc.) - open the raw file instead.
      const url = window.URL.createObjectURL(new Blob([blob], { type: contentType }))
      window.open(url, '_blank')
      setTimeout(() => window.URL.revokeObjectURL(url), 10000)
    } catch (error) {
      setPreviewLoading(false)
      showKAlert('danger', 'previewError')
    }
  }

  const handlePreviewClose = () => {
    setPreviewType(null)
    setPreviewText(null)
    setPreviewHtml(null)
    setPreviewFile(null)
    setPreviewFilename(null)
    setPreviewLoading(false)
  }

  const isDsSignTeamAvailable = async () => {
    const res = await isDsSignAvailable()
    setDsSignAvailable(res.signTeam)
  }

  const notifyMainAdmin = async () => {
    await fetch(`${process.env.REACT_APP_API_URL}/user/main/notification`, {
      headers: { 'Content-Type': 'application/json' },
    })
  }

  useEffect(() => {
    isDsSignTeamAvailable()
  }, [])

  // True when the section holds a document received within the last year - used to keep the
  // matching register button green (outline-success) until a fresh extract is due.
  const hasValidRegisterDocument = (section: DocumentFile['type']): boolean =>
    (docsBySection[section] || []).some(doc => {
      if (!doc.uploadedAt) return false
      const receivedAt = new Date(doc.uploadedAt).getTime()
      return Number.isFinite(receivedAt) && Date.now() - receivedAt < REGISTER_DOCUMENT_VALIDITY_MS
    })

  // Register buttons render as an outline until a document is on file; keep the primary
  // outline styling in the neutral state and let the green variant take over otherwise.
  const registerButtonClassName = (active: boolean, base: string): string =>
    active ? base : `${base} text-primary border-primary`.trim()

  // Status buttons show success/danger as a colored border, not a fully filled button -
  // Bootstrap's outline-* variants keep the white background and only color border + text.
  const toOutlineVariant = (
    variant: ActionButtonConfig['variant']
  ): ActionButtonConfig['variant'] => {
    if (variant === 'success') return 'outline-success'
    if (variant === 'danger') return 'outline-danger'
    if (variant === 'warning') return 'outline-warning'
    return variant
  }

  const transparenzButtonVariant: 'white' | 'success' | 'warning' | 'secondary' | 'danger' =
    client?.transparenzregister?.alreadyAvailable || client?.transparenzregister?.notRequired
      ? 'success'
      : client?._id
        ? variantTransparenz[client._id] || 'white'
        : 'white'
  const isAdmin = !!userAccess && userAccess.permissions.admin
  const isCompany = client?.isPerson === 0

  const identityButton = {
    group: 'OTHER',
    id: 'identity',
    show: client?.isPerson === 1 && isAdmin,
    size: 'lg',
    icon: 'person-bounding-box',
    label: t('DS | Identity'),
    variant: (variantIdentity[client?._id || ''] || 'white') as
      'white' | 'success' | 'warning' | 'danger',
    title: t('DS | Identity'),
    onClick: () => {
      if (isPiiEncryptionEnabled()) {
        showKAlert('danger', t('serviceDisabledByPiiEncryption'))
        return
      }
      setAccessSubtitle(makeSubtitle('DS|Identity', serviceCosts?.identity))
      // The identity modal (and createIdentityRequest) read these straight from
      // localStorage, so seed every one from this client - otherwise a field is
      // blank or shows a previously opened client's value.
      localStorage.setItem('name', client?.name || '')
      localStorage.setItem('firstName', client?.firstName || '')
      localStorage.setItem('dateOfBirth', client?.dateOfBirth || '')
      localStorage.setItem('gender', client?.gender || '')
      localStorage.setItem('clientEmail', client?.email || '')
      localStorage.setItem('country', client?.country || '')
      localStorage.setItem('clientId', client?._id || '')
      getAccess('identity')
      setPendingAction(() => async () => setShowModal(true))
      // setShowModal(true)
    },
  }

  const makeSubtitle = (label: string, points: number | undefined): ReactNode => (
    <ServiceCostSubtitle label={label} points={points} />
  )

  const transparenzDeDocumentValid = hasValidRegisterDocument('transparenzregister')
  const handelsregisterDeDocumentValid = hasValidRegisterDocument('Handelsregister')
  const transparenzChDocumentValid = hasValidRegisterDocument('transparenz-ch')
  const handelsregisterChDocumentValid = hasValidRegisterDocument('zefix')

  const transparenzDeButtonGreen =
    transparenzButtonVariant === 'success' || transparenzDeDocumentValid

  const actionButtons: ActionButtonConfig[] = [
    {
      id: 'transparenzregister-de',
      group: 'DE',
      show: isAdmin && isCompany,
      size: 'lg',
      icon: 'file-earmark-text',
      label: 'Transparenzregister DE',
      variant:
        transparenzButtonVariant === 'success'
          ? 'success'
          : transparenzDeDocumentValid
            ? 'outline-success'
            : transparenzButtonVariant,
      className: registerButtonClassName(transparenzDeButtonGreen, 'align-left px-5'),
      title: t('Dokumente aus Transparenzregister bestellen'),
      onClick: () => {
        setAccessSubtitle(
          makeSubtitle('Auszug Transparenzregister', serviceCosts?.transparenzregister)
        )
        setPendingAction(() => async () => setTransparenzModal(true))
        getAccess('transparenzregister')
      },
    },
    {
      id: 'handelsregister-de',
      group: 'DE',
      show: isAdmin && isCompany,
      size: 'lg',
      icon: 'file-earmark-text',
      label: 'Handelsregister Deutschland',
      variant: handelsregisterDeDocumentValid ? 'outline-success' : 'white',
      className: registerButtonClassName(handelsregisterDeDocumentValid, 'align-left px-5'),
      title: t('Dokumente aus Handelsregister bestellen'),
      onClick: () => {
        setAccessSubtitle(makeSubtitle('Handelsregister', serviceCosts?.handelsregister))
        setPendingAction(() => async () => setAmlModal(true))
        getAccess('handelsregister')
      },
    },
    {
      id: 'transparenzregister-ch',
      group: 'CH',
      show: isAdmin && isCompany,
      size: 'lg',
      icon: 'people',
      label: t('transparenzChTitle'),
      variant: transparenzChDocumentValid ? 'outline-success' : 'white',
      className: registerButtonClassName(transparenzChDocumentValid, 'align-left px-5'),
      title: t('Dokumente aus Transparenzregister bestellen'),
      onClick: () => {
        if (!globalSettings.transparenzregisterChEnabled) {
          showKAlert(
            'info',
            'Die Anbindung an das Transparenzregister Schweiz ist unsererseits vorbereitet. ' +
              'Das Bundesamt für Justiz stellt das Transparenzregister bzw. den technischen Zugang ' +
              'derzeit jedoch noch nicht zur Verfügung.\n\n' +
              'Eine Abfrage ist deshalb aktuell noch nicht möglich. Sobald das Bundesamt für Justiz ' +
              'den Zugang freigibt, steht Ihnen die Funktion zur Verfügung.'
          )
          return
        }

        setAccessSubtitle(
          makeSubtitle('Auszug Transparenzregister CH', serviceCosts?.['transparenzregister-ch'])
        )
        setPendingAction(() => async () => setShowTransparenzChModal(true))
        getAccess('transparenzregister-ch')
      },
    },
    {
      id: 'zefix',
      group: 'CH',
      show: isAdmin && isCompany,
      icon: 'braces',
      size: 'lg',
      label: t('zefixCheck') + ' CH',
      variant: handelsregisterChDocumentValid ? 'outline-success' : 'white',
      className: registerButtonClassName(handelsregisterChDocumentValid, ''),
      title: t('zefixCheck'),
      onClick: () => {
        setPendingAction(() => async () => {
          const clientId = client?._id.toString()
          if (clientId) {
            const existing = await zefixCompanyExtractByClientId(clientId)
            if (existing?.[0]?.uid) {
              setZefixInitialDetails(existing[0])
              setShowZefixModal(true)
              return
            }
          }
          if (client) {
            const results = await zefixCompanySearch(client.name)
            setZefixResults(Array.isArray(results) ? results : [])
            setShowZefixModal(true)
          }
        })
        setAccessSubtitle(makeSubtitle('Zefix', serviceCosts?.['handelsregister-ch']))
        getAccess('handelsregister-ch')
      },
    },

    {
      id: 'zefix-auditlog-ch',
      group: 'CH',
      show: isAdmin && isCompany,
      size: 'sm',
      icon: 'clock-history',
      variant: 'white',
      className: 'text-primary',
      onlyIcon: true,
      title: t('zefixAuditLog'),
      onClick: () => setShowAuditLogModal(true),
    },
    {
      id: 'uid-check-ch',
      group: 'CH',
      show: isAdmin && isCompany,
      size: 'lg',
      icon: 'building-check',
      label: t('uidCheck') + ' CH',
      variant: uidCheckVariant,
      className: uidCheckVariant === 'white' ? 'text-primary border-primary' : '',
      title: t('uidCheckTitle'),
      onClick: () => {
        setAccessSubtitle(makeSubtitle('UID', serviceCosts?.uid))
        setPendingAction(() => async () => setShowUidModal(true))
        getAccess('uid')
      },
    },
    {
      id: 'uid-auditlog-ch',
      group: 'CH',
      show: isAdmin && isCompany,
      size: 'sm',
      icon: 'clock-history',
      variant: 'white',
      className: 'text-primary',
      title: t('uidAuditLog'),
      onlyIcon: true,
      onClick: () => setShowUidAuditLogModal(true),
    },
    {
      id: 'topograph',
      group: 'OTHER',
      show: isAdmin && isCompany,
      icon: 'building',
      size: 'lg',
      label: `KYB - ${t('Unternehmensdaten')}`,
      variant: 'white',
      className: 'text-primary border-primary',
      title: 'Unternehmensdaten',
      onClick: () => {
        setAccessTitle('KYB - ' + t('Unternehmensdaten'))
        setAccessWarningText('kybWarningText')
        setPendingAction(() => async () => setShowTopographModal(true))
        getAccess('topograph')
      },
    },
  ]

  const visibleActionButtons = actionButtons.filter(action => action.show)
  const deButtons = visibleActionButtons.filter(action => action.group === 'DE')
  const chButtons = visibleActionButtons.filter(action => action.group === 'CH')
  const otherButtons = visibleActionButtons.filter(action => action.group === 'OTHER')

  async function loadSharePointFiles(clientId: string) {
    try {
      const result = await getSharePointFiles(clientId)
      setSharePointFiles(Array.isArray(result.files) ? result.files : [])
    } catch {
      setSharePointFiles([])
    }
  }

  async function handleSharePointFileSelect(item: SharePointDriveItem) {
    setShowSharePointModal(false)

    const clientId = client?._id?.toString() || localStorage.getItem('clientId') || ''

    if (!clientId) return

    try {
      const result = await linkSharePointFile({ clientId, itemId: item.id })

      if (result.ok) {
        showKAlert('success', 'sharePointFileLinked')
        await loadSharePointFiles(clientId)
      }
    } catch {
      // The global fetch wrapper surfaces the error alert.
    }
  }

  async function handleOpenSharePointFile(id: string) {
    try {
      const result = await openSharePointFile(id)

      if (result.changed) {
        showKAlert('warning', 'sharePointFileChanged')
      }

      if (result.file?.webUrl) {
        window.open(result.file.webUrl, '_blank', 'noopener,noreferrer')
      }
    } catch {
      // The global fetch wrapper surfaces the error alert.
    }
  }

  async function handleDeleteSharePointFile(id: string) {
    const result = await deleteSharePointFile(id)

    if (result.ok) {
      const clientId = client?._id?.toString() || localStorage.getItem('clientId') || ''

      if (clientId) await loadSharePointFiles(clientId)
    }
  }

  async function handleHashSharePointFile(id: string) {
    const clientId = client?._id?.toString() || localStorage.getItem('clientId') || ''

    const result = await hashSharePointFile(id)

    if (result.contentHash) {
      showKAlert('success', 'sharePointHashComputed')

      if (clientId) await loadSharePointFiles(clientId)
    }
  }

  async function handleCopySharePointFile(id: string) {
    try {
      const result = await copySharePointFile(id)

      if (result.ok) {
        showKAlert('success', 'sharePointFileCopied')
        await loadClient()
      }
    } catch {
      // The global fetch wrapper surfaces the error alert.
    }
  }

  async function handleUploadDocumentToSharePoint(doc: DocumentFile) {
    const clientId = client?._id?.toString() || localStorage.getItem('clientId') || ''

    if (!clientId) return

    try {
      const result = await uploadDocumentToSharePoint({
        clientId,
        // Use the document's real storage type, not the display section: docs from
        // types not listed in SECTIONS are shown under `ds-sign` but live in their
        // own directory (DocumentsForm.tsx loadClient grouping).
        type: doc.type,
        filename: doc.name,
      })

      if (result.ok) {
        showKAlert('success', 'sharePointDocumentUploaded')
      }
    } catch {
      // The global fetch wrapper surfaces the error alert.
    }
  }

  return (
    <Container>
      <Row>
        <div className='sidebar col-md-3 col-lg-2 p-0'>
          <KLayerNavigation logged={isUserLoggedIn()} />
        </div>

        <Col sm={8} md={9} lg={10}>
          <CardTitle className='h4 mb-0'>{t('Dokumente/Identifikation')}</CardTitle>
          <h5 className='text-muted mb-4'>
            {client?.clientNumber} {client?.firstName} {client?.name}
          </h5>

          <CardBody className='mt-3'>
            {identityButton.show && (
              <Row className='g-3 p-3 rounded'>
                <Col md={4}>
                  <div className='d-flex flex-column mb-4'>
                    <div className='flex-column'>
                      <KButton
                        id={identityButton.id}
                        type='button'
                        size={identityButton.size}
                        icon={identityButton.icon}
                        label={identityButton.label}
                        variant={toOutlineVariant(identityButton.variant)}
                        className={`documents-action-btn flex-grow-1 ${identityButton.variant === 'white' ? 'text-primary border-primary' : ''}`.trim()}
                        title={identityButton.title}
                        onClick={identityButton.onClick}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            {visibleActionButtons.length > 0 && (
              <Row className='g-3 p-3 bg-light'>
                {deButtons.length > 0 && (
                  <Col md={4}>
                    <div className='p-2'>
                      <div className='small text-muted fw-semibold mb-1'>{t('Germany')}</div>
                      <div className='d-flex flex-column gap-2'>
                        {deButtons
                          .reduce<ActionButtonConfig[][]>((rows, action) => {
                            if (action.onlyIcon && rows.length > 0) {
                              rows[rows.length - 1].push(action)
                            } else {
                              rows.push([action])
                            }
                            return rows
                          }, [])
                          .map(row => (
                            <div key={row[0].id} className='d-flex align-items-center gap-1'>
                              {row.map((action, idx) => (
                                <KButton
                                  key={action.id}
                                  id={action.id}
                                  type='button'
                                  size={action.size}
                                  icon={action.icon}
                                  label={action.onlyIcon ? undefined : action.label}
                                  variant={toOutlineVariant(action.variant)}
                                  className={`${action.className || ''} ${idx === 0 ? 'documents-action-btn flex-grow-1' : ''}`.trim()}
                                  title={action.title}
                                  onClick={action.onClick}
                                />
                              ))}
                            </div>
                          ))}
                      </div>
                    </div>
                  </Col>
                )}

                {chButtons.length > 0 && (
                  <Col xs={12} md={deButtons.length > 0 ? 4 : 12}>
                    <div className='rounded p-2'>
                      <div className='small text-muted fw-semibold mb-1'>{t('Switzerland')}</div>
                      <div className='d-flex flex-column gap-2'>
                        {chButtons
                          .reduce<ActionButtonConfig[][]>((rows, action) => {
                            if (action.onlyIcon && rows.length > 0) {
                              rows[rows.length - 1].push(action)
                            } else {
                              rows.push([action])
                            }
                            return rows
                          }, [])
                          .map(row => (
                            <div key={row[0].id} className='d-flex align-items-center gap-1'>
                              {row.map((action, idx) => (
                                <KButton
                                  key={action.id}
                                  id={action.id}
                                  type='button'
                                  size={action.size}
                                  icon={action.icon}
                                  label={action.onlyIcon ? undefined : action.label}
                                  variant={toOutlineVariant(action.variant)}
                                  className={`${action.className || ''} ${idx === 0 ? 'documents-action-btn flex-grow-1' : ''}`.trim()}
                                  title={action.title}
                                  onClick={action.onClick}
                                />
                              ))}
                            </div>
                          ))}
                      </div>
                    </div>
                  </Col>
                )}
                {otherButtons.length > 0 && (
                  <Col xs={12} md={deButtons.length > 0 ? 4 : 12}>
                    <div className='rounded p-2'>
                      <div className='small text-muted fw-semibold mb-1'>{t('otherButtons')}</div>
                      <div className='d-flex flex-column gap-2'>
                        {otherButtons
                          .reduce<ActionButtonConfig[][]>((rows, action) => {
                            if (action.onlyIcon && rows.length > 0) {
                              rows[rows.length - 1].push(action)
                            } else {
                              rows.push([action])
                            }
                            return rows
                          }, [])
                          .map(row => (
                            <div key={row[0].id} className='d-flex align-items-center gap-1'>
                              {row.map((action, idx) => (
                                <KButton
                                  key={action.id}
                                  id={action.id}
                                  type='button'
                                  size={action.onlyIcon ? 'sm' : action.size}
                                  icon={action.icon}
                                  label={action.onlyIcon ? undefined : action.label}
                                  variant={toOutlineVariant(action.variant)}
                                  className={`${action.className || ''} ${idx === 0 ? 'documents-action-btn flex-grow-1' : ''}`.trim()}
                                  title={action.title}
                                  onClick={action.onClick}
                                />
                              ))}
                            </div>
                          ))}
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            )}
            <div className='d-flex align-items-center justify-content-between mb-2 mt-4 pb-2'>
              <h4 className='mb-0 fw-semibold'>{t('documents')}</h4>
            </div>

            <div className='mt-3 mb-3'>
              {expandedSections['ds-sign'] && isAdmin && (
                <div className='d-flex align-items-center gap-2'>
                  <FileUploader
                    clientId={localStorage.getItem('clientId') || ''}
                    firmename={client?.name || ''}
                    onUploadSuccess={loadClient}
                  />

                  {microsoftEnabled && (
                    <Button
                      type='button'
                      variant='outline-primary'
                      onClick={() => setShowSharePointModal(true)}
                    >
                      <i className='bi bi-microsoft me-2' />
                      {t('addFromSharePoint')}
                    </Button>
                  )}
                </div>
              )}
            </div>

            {microsoftEnabled && sharePointFiles.length > 0 && (
              <div className='mt-3 mb-4'>
                <h6 className='fw-semibold'>{t('sharePointLinkedFiles')}</h6>
                <ListGroup>
                  {sharePointFiles.map(file => (
                    <ListGroup.Item
                      key={file.id}
                      className='d-flex align-items-center justify-content-between'
                    >
                      <div className='d-flex align-items-center'>
                        <i className='bi bi-file-earmark text-primary me-3 fs-5' />
                        <div>
                          <div className='fw-medium'>{file.name}</div>
                          {file.lastModifiedDateTime && (
                            <div className='small text-muted'>
                              {t('sharePointLastModified')}: {formatDate(file.lastModifiedDateTime)}
                            </div>
                          )}
                          {!!file.contentHash && (
                            <div className='small text-muted text-break'>
                              {t('sharePointContentHash')} ({file.contentHashAlgorithm ?? 'sha256'}
                              ): {file.contentHash}
                            </div>
                          )}
                          <div className='small text-muted'>
                            {t('sharePointReviewedOn')}:{' '}
                            {formatDate(file.lastVerifiedAt ?? file.createdAt)}
                          </div>
                        </div>
                      </div>
                      <div className='d-flex gap-2'>
                        <Button
                          type='button'
                          size='sm'
                          variant='outline-primary'
                          onClick={() => handleOpenSharePointFile(file.id)}
                        >
                          <i className='bi bi-box-arrow-up-right me-1' />
                          {t('Open')}
                        </Button>
                        <Button
                          type='button'
                          size='sm'
                          variant='outline-secondary'
                          title={t('sharePointComputeHash')}
                          onClick={() => handleHashSharePointFile(file.id)}
                        >
                          <i className='bi bi-fingerprint' />
                        </Button>
                        <Button
                          type='button'
                          size='sm'
                          variant='outline-primary'
                          title={t('sharePointCopyToTool')}
                          onClick={() => handleCopySharePointFile(file.id)}
                        >
                          <i className='bi bi-download' />
                        </Button>
                        <Button
                          type='button'
                          size='sm'
                          variant='outline-danger'
                          onClick={() => handleDeleteSharePointFile(file.id)}
                        >
                          <i className='bi bi-trash' />
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            )}

            <div className='mb-3'>
              <Button
                variant={dsSignFilter === 'all' ? 'primary' : 'outline-primary'}
                size='sm'
                className='me-2'
                onClick={() => setDsSignFilter('all')}
              >
                {t('Alle')}
              </Button>
              <Button
                variant={dsSignFilter === 'signed' ? 'success' : 'outline-success'}
                size='sm'
                className='me-2'
                onClick={() => setDsSignFilter('signed')}
              >
                {t('Unterzeichnet')}
              </Button>
              <Button
                variant={dsSignFilter === 'unsigned' ? 'secondary' : 'outline-secondary'}
                size='sm'
                onClick={() => setDsSignFilter('unsigned')}
              >
                {t('unsigned')}
              </Button>
            </div>

            {SECTIONS.map(section => {
              const docs = docsBySection[section]
              if (!docs || docs.length === 0) return null

              let filteredDocs = docs

              if (section === 'ds-sign') {
                filteredDocs = docs.filter(doc => {
                  const matched = findSignRequest(doc)

                  if (dsSignFilter === 'signed') return matched?.status === 'finished'
                  if (dsSignFilter === 'unsigned') return matched?.status !== 'finished'
                  return true
                })
              }

              if (filteredDocs.length === 0) return null

              const isExpanded = expandedSections[section]

              return (
                <div className='document-section-compact mb-4' key={section}>
                  <h5
                    className='section-title d-flex justify-content-between align-items-center sticky-top bg-white py-2 px-1 border-bottom shadow-sm z-2'
                    onClick={() => toggleSection(section)}
                    style={{ cursor: 'pointer' }}
                  >
                    <span>
                      {isExpanded ? (
                        <ChevronDown className='me-2' />
                      ) : (
                        <ChevronRight className='me-2' />
                      )}
                      {handleLabel(section)}
                    </span>
                    <span className='badge bg-secondary'>{docs.length}</span>
                  </h5>

                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0, backgroundColor: '#f9f9f9' }}
                        animate={{ height: 'auto', opacity: 1, backgroundColor: '#fff' }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className='p-2 border rounded'
                      >
                        <div className='table-responsive'>
                          <table className='table table-sm align-middle document-table'>
                            <thead>
                              <tr>
                                <th>{t('documentName')}</th>
                                <th>{t('uploadedAt')}</th>
                                <th>{t('uploadedBy')}</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredDocs.map((doc, idx) => {
                                const matchedRequest = findSignRequest(doc)
                                return (
                                  <motion.tr
                                    key={idx}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: idx * 0.05 }}
                                    className='doc-row'
                                  >
                                    <td className='doc-name'>
                                      <i className='bi bi-file-earmark-pdf me-2 text-danger' />
                                      {doc.name}
                                      {matchedRequest?.status && (
                                        <>
                                          <span
                                            className={`${getStatusClass(matchedRequest.status)} ms-2`}
                                          >
                                            {t(matchedRequest.status)}
                                          </span>
                                          <span>
                                            &nbsp;{t('requestCreatedBy')}:{' '}
                                            {userNames[matchedRequest.user_id] || ''}
                                          </span>
                                        </>
                                      )}
                                    </td>

                                    <td className='text-nowrap text-muted small'>
                                      {doc.uploadedAt
                                        ? new Date(doc.uploadedAt).toLocaleDateString('de-DE', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                          })
                                        : '—'}
                                    </td>

                                    <td className='text-nowrap text-muted small'>
                                      {doc.uploadedBy || '—'}
                                    </td>

                                    <td className='text-end text-nowrap'>
                                      {protocolFiles[doc.name] && (
                                        <KButton
                                          variant='outline-primary'
                                          className='btn-sm me-2'
                                          icon='file-earmark-text'
                                          title={t('protocol')}
                                          onClick={() =>
                                            handleDownload(protocolFiles[doc.name], section)
                                          }
                                        />
                                      )}
                                      {graphDataFiles[doc.name] && (
                                        <KButton
                                          variant='outline-primary'
                                          className='btn-sm me-2'
                                          icon='file-earmark-text'
                                          title={t('topographGraphData')}
                                          onClick={() =>
                                            handleDownload(graphDataFiles[doc.name], section)
                                          }
                                        />
                                      )}
                                      <KButton
                                        className='me-2 text-white'
                                        icon='eye'
                                        variant='collapse'
                                        onClick={() => handlePreview(doc, section)}
                                        title={t('preview')}
                                      >
                                        <i className='bi bi-eye'></i>
                                      </KButton>
                                      <KButton
                                        className='me-2 text-white'
                                        icon='download'
                                        variant='success'
                                        title={t('download')}
                                        onClick={() => handleDownload(doc.name, section)}
                                      />
                                      {isAdmin && microsoftEnabled && (
                                        <KButton
                                          className='me-2 text-white'
                                          icon='microsoft'
                                          variant='primary'
                                          title={t('sharePointUploadToSharePoint')}
                                          onClick={() => handleUploadDocumentToSharePoint(doc)}
                                        />
                                      )}
                                      {section === 'ds-sign' && isAdmin && (
                                        <KButton
                                          id={`sign-${idx}`}
                                          variant={
                                            matchedRequest?.status === 'finished'
                                              ? 'success'
                                              : matchedRequest
                                                ? 'danger'
                                                : 'primary'
                                          }
                                          className='btn-sm me-2'
                                          icon='pencil-square'
                                          title={t('DS|Sign')}
                                          onClick={async () => {
                                            if (isPiiEncryptionEnabled()) {
                                              showKAlert(
                                                'danger',
                                                t('serviceDisabledByPiiEncryption')
                                              )
                                              return
                                            }
                                            if (!dsSignAvailable) {
                                              await notifyMainAdmin()
                                              showKAlert('danger', t('dsSignTeamNotAvailable'))
                                              return
                                            }
                                            setPendingAction(() => async () => {
                                              setCurrentDocument(doc.name)
                                              setSignModal(true)
                                            })
                                            getAccess()
                                          }}
                                        />
                                      )}
                                      {section === 'ds-sign' && (
                                        <KButton
                                          id={`cancel-${idx}`}
                                          variant='warning'
                                          className='btn-sm me-2'
                                          icon='x-circle'
                                          title={t('dsSignCancel')}
                                          onClick={() =>
                                            matchedRequest?.document_id &&
                                            cancel(matchedRequest.document_id)
                                          }
                                        />
                                      )}

                                      <Button
                                        variant='outline-danger'
                                        size='sm'
                                        title={t('deleteDocument')}
                                        onClick={() => handleDelete(doc, section)}
                                        className='me-2'
                                      >
                                        <i className='bi bi-trash'></i>
                                      </Button>
                                    </td>
                                  </motion.tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
          </CardBody>

          {signModal && (
            <KSignModal
              show={signModal}
              onClose={handleSignClose}
              onAccept={handleSignClose}
              firmename={client?.name || ''}
              documentName={currentDocument}
              client={client || null}
            />
          )}

          <AmlOrderModal
            show={amlModal}
            onClose={() => {
              setAmlModal(false)
            }}
            clientId={client?._id || localStorage.getItem('clientId')}
          />

          <TransparenzregisterModal
            show={transparenzModal}
            onClose={() => {
              setTransparenzModal(false)
              window.location.reload()
            }}
            client={client}
            clientName={client?.name || ''}
            registernummer={client?.registernummer || ''}
            userEmail={localStorage.getItem('email') || ''}
            onSuccess={() => {
              if (client?._id) fetchTransparenzStatusForClient(client._id)
            }}
          />

          <Modal show={!!previewType} onHide={handlePreviewClose} size='lg' centered>
            <Modal.Header closeButton>
              <Modal.Title>{previewFilename ?? t('preview')}</Modal.Title>
              {previewType === 'excel' && (
                <Button
                  variant='outline-secondary'
                  size='sm'
                  className='ms-auto me-2'
                  onClick={() => setIsOpenExcelPreview(prev => !prev)}
                >
                  <i className={`bi bi-${isOpenExcelPreview ? 'sun' : 'moon'}`} />
                </Button>
              )}
            </Modal.Header>
            <Modal.Body style={{ height: '80vh', overflow: 'auto' }}>
              {previewLoading && (
                <div className='d-flex justify-content-center align-items-center h-100'>
                  <Spinner animation='border' />
                </div>
              )}

              {!previewLoading && previewType === 'txt' && (
                <pre className='p-3 mb-0' style={{ whiteSpace: 'pre-wrap' }}>
                  {previewText}
                </pre>
              )}

              {previewType === 'excel' && previewFile && (
                <XlsxViewerPreview
                  file={previewFile}
                  fileName={previewFilename ?? undefined}
                  height='100%'
                  isDark={isOpenExcelPreview}
                />
              )}

              {!previewLoading && previewType === 'word' && previewHtml && (
                <div className='p-2' dangerouslySetInnerHTML={{ __html: previewHtml }} />
              )}
            </Modal.Body>
          </Modal>
        </Col>
      </Row>

      {showModal && (
        <div className='App'>
          <IdentityModal
            show={showModal}
            onClose={handleIdentityClose}
            onAccept={createIdentityRequest}
            onDelete={removeIdentityRequest}
            existingIdentity={identityRequests[client?._id || '']}
          />
        </div>
      )}

      {access && (
        <KAccess
          show={access}
          title={accessTitle ?? (pinRequired ? t('pinCodeSent') : t('confirmAction'))}
          text={accessText}
          warningText={accessWarningText}
          subtitle={accessSubtitle}
          pin={pin}
          pinRequired={pinRequired}
          onConfirm={(pin: any) => {
            checkPin(pin)
          }}
          onClose={handleAccessClose}
        />
      )}

      {showZefixModal && (
        <ZefixResultModal
          show={showZefixModal}
          results={zefixResults}
          client={client}
          clientId={client?._id.toString()}
          initialDetails={zefixInitialDetails}
          onClose={() => {
            setShowZefixModal(false)
            setZefixInitialDetails(null)
            setZefixResults([])
          }}
        />
      )}

      {showAuditLogModal && (
        <ZefixAuditLogModal
          show={showAuditLogModal}
          clientId={client?._id.toString()}
          onClose={() => setShowAuditLogModal(false)}
        />
      )}

      <UidCheckModal
        show={showUidModal}
        clientId={client?._id?.toString()}
        client={client}
        onClose={() => setShowUidModal(false)}
        onCheckResult={status => setUidCheckVariant(status)}
      />

      <UidAuditLogModal
        show={showUidAuditLogModal}
        clientId={client?._id?.toString() ?? ''}
        onClose={() => setShowUidAuditLogModal(false)}
      />

      <TransparenzChModal
        show={showTransparenzChModal}
        clientId={client?._id}
        clientName={client?.name}
        clientUid={client?.uid}
        onClose={() => setShowTransparenzChModal(false)}
      />

      <SharePointFilePickerModal
        show={showSharePointModal}
        onHide={() => setShowSharePointModal(false)}
        onSelect={handleSharePointFileSelect}
      />

      <TopographModal
        show={showTopographModal}
        clientName={client?.name}
        clientId={client?._id?.toString()}
        onClose={() => setShowTopographModal(false)}
      />
    </Container>
  )
}
